import { html, LitElement, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { ViewType } from '../../types/types';
import { FontSizes, FontWeights } from '../../../types/common/types';
import {
  DISCARD_BUTTON_ID,
  GZ_TITLE_ID_SELECTOR,
  SAVE_AS_BUTTON_ID,
  SAVE_BUTTON_ID,
  SmartViewButtonEvents,
  SmartViewToolbarState,
} from '../types';
import { ButtonColors, ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../ui/gzButton/types';
import { connect } from 'pwa-helpers';
import { RootState, store } from '../../../redux/store';
import {
  selectActiveViewConfiguration,
  selectDashboardHasUnsavedChanges,
} from '../../redux/dashboards.smart.views.slice';
import { translateFn } from '../../../utils/translator.helper';
import { DashboardAppState, selectDashboardsAppState } from '../../redux/dashboards.app.reducer';
import { fullScreenActiveStateSelector } from '../../redux/dashboards.ui.slice';
import { classMap } from 'lit/directives/class-map.js';

/**
 * @summary Smart views toolbar with view name & save/save as/discard actions
 * @tag gzDashboardSmartViewsToolbar
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('gz-dashboard-smart-views-toolbar')
export class GzDashboardSmartViewsToolbarWebComponent extends connect(store)(LitElement) {
  /**
   * Controls the state of the buttons in pair with toolbar state
   * @type {ViewType}
   */
  @property({
    type: Number,
  })
  private viewType: ViewType;

  @property({
    type: String,
  })
  private viewName = '';

  /**
   * Controls the state of the buttons in pair with view type
   * @type {SmartViewToolbarState}
   */
  @property({
    type: Number,
  })
  private toolbarState: SmartViewToolbarState;

  /**
   * Text for save button
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  public saveButtonLabel = translateFn('Save#preserve state');

  /**
   * Text for save as button
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  public saveAsButtonLabel = translateFn('Save as');

  /**
   * Text for discard button
   */
  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  public discardButtonLabel = translateFn('Discard changes');

  /**
   * @internal
   * @type {string}
   * @private
   */
  @state()
  private propsChangedCharacter = '';

  @state()
  private fullScreenActive = false;

  /**
   *@internal
   */
  @query(`#${GZ_TITLE_ID_SELECTOR}`)
  private titleElem;

  /**
   *@internal
   */
  @query(`#${SAVE_BUTTON_ID}`)
  private saveButtonElem;

  /**
   *@internal
   */
  @query(`#${SAVE_AS_BUTTON_ID}`)
  private saveAsButtonElem;

  /**
   *@internal
   */
  @query(`#${DISCARD_BUTTON_ID}`)
  private discardButtonElem;

  static get styles() {
    return styles;
  }

  stateChanged(rootState: RootState) {
    const dashboardsAppState: DashboardAppState = selectDashboardsAppState(rootState);
    const activeViewConfiguration = selectActiveViewConfiguration(dashboardsAppState);
    this.fullScreenActive = fullScreenActiveStateSelector(dashboardsAppState);
    this.toolbarState = selectDashboardHasUnsavedChanges(dashboardsAppState)
      ? SmartViewToolbarState.UNSAVED_CHANGES
      : SmartViewToolbarState.DEFAULT;
    this.viewName = activeViewConfiguration.label;
    this.viewType = activeViewConfiguration.type;
  }

  render(): TemplateResult {
    const actionsClasses = { actions: true, hidden: this.fullScreenActive };
    return html`
      <div class="smart-views-toolbar">
        <div class="title">
          <gz-title id="${GZ_TITLE_ID_SELECTOR}" fontWeight="${FontWeights.MEDIUM}" fontSize="${FontSizes.HEADING_4}"
            >${this.viewName}${this.propsChangedCharacter}
          </gz-title>
        </div>
        <div class="${classMap(actionsClasses)}">
          <gz-button
            id="${SAVE_BUTTON_ID}"
            type="${ButtonTypes.QUIET}"
            color="${ButtonColors.SECONDARY}"
            buttonheight="${ButtonHeights.SMALL}"
            buttonfontsize="${ButtonFontSizes.SMALL}"
            @click="${this.saveButtonClicked}"
            lowercase
            >${this.saveButtonLabel}
          </gz-button>
          <gz-button
            id="${SAVE_AS_BUTTON_ID}"
            type="${ButtonTypes.QUIET}"
            color="${ButtonColors.SECONDARY}"
            buttonheight="${ButtonHeights.SMALL}"
            buttonfontsize="${ButtonFontSizes.SMALL}"
            @click="${this.saveAsButtonClicked}"
            lowercase
            >${this.saveAsButtonLabel}
          </gz-button>
          <gz-button
            id="${DISCARD_BUTTON_ID}"
            type="${ButtonTypes.QUIET}"
            color="${ButtonColors.SECONDARY}"
            buttonheight="${ButtonHeights.SMALL}"
            buttonfontsize="${ButtonFontSizes.SMALL}"
            @click="${this.discardButtonClicked}"
            disabled
            lowercase
            >${this.discardButtonLabel}
          </gz-button>
          <div class="divider" />
        </div>
      </div>
    `;
  }

  protected updated(changedProperties: PropertyValues) {
    this.handlePropsChanged();
  }

  private handlePropsChanged() {
    switch (this.viewType) {
      case ViewType.DEFAULT:
        this.handlePropsChangedDefaultView();
        break;
      case ViewType.CUSTOM:
        this.handlePropsChangedCustomView();
        break;
      default:
        throw new Error(`Unknown view type: ${this.viewType}`);
    }
  }

  private handlePropsChangedCustomView() {
    switch (this.toolbarState) {
      case SmartViewToolbarState.UNSAVED_CHANGES:
        this.enableDiscardButton();
        this.enableSaveButton();
        this.appendAsteriskToViewName();
        break;
      case SmartViewToolbarState.DEFAULT:
        this.disableDiscardButton();
        this.disableSaveButton();
        this.removeAsteriskFromViewName();
        break;
      default:
        throw new Error(`Unknown toolbar state: ${this.toolbarState}`);
    }
  }

  private removeAsteriskFromViewName() {
    this.propsChangedCharacter = '';
  }

  private disableSaveButton() {
    this.saveButtonElem.disabled = true;
  }

  private disableDiscardButton() {
    this.discardButtonElem.disabled = true;
  }

  private appendAsteriskToViewName() {
    this.propsChangedCharacter = '*';
  }

  private enableSaveButton() {
    this.saveButtonElem.removeAttribute('disabled');
  }

  private enableDiscardButton() {
    this.discardButtonElem.removeAttribute('disabled');
  }

  private handlePropsChangedDefaultView() {
    this.disableSaveButton();
    switch (this.toolbarState) {
      case SmartViewToolbarState.UNSAVED_CHANGES:
        this.enableDiscardButton();
        this.appendAsteriskToViewName();
        break;
      case SmartViewToolbarState.DEFAULT:
        this.disableDiscardButton();
        this.removeAsteriskFromViewName();
        break;
      default:
        throw new Error(`Unknown toolbar state: ${this.toolbarState}`);
    }
  }

  /**
   * @event { CustomEvent } - 'save' fired when clicking discard button
   */
  private saveButtonClicked() {
    this.dispatchEvent(
      new CustomEvent(SmartViewButtonEvents.SAVE, {
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * @event { CustomEvent } - 'saveAs' fired when clicking discard button
   */
  private saveAsButtonClicked() {
    this.dispatchEvent(
      new CustomEvent(SmartViewButtonEvents.SAVE_AS, {
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * @event { CustomEvent } - 'discard' fired when clicking discard button
   */
  private discardButtonClicked() {
    this.dispatchEvent(
      new CustomEvent(SmartViewButtonEvents.DISCARD, {
        bubbles: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-dashboard-smart-views-toolbar': GzDashboardSmartViewsToolbarWebComponent;
  }
}
